import type {
  PlatformWebModuleContribution,
  PlatformWebRouteContribution,
  PlatformWebSettingEditorContribution,
} from '@crest/platform-web/module-runtime';
import {
  definePlatformModuleContributionMetadata,
  type PlatformModuleContributionMetadata,
} from '@crest/platform-contracts/modules';
import type { PlatformNavigationItem } from '@crest/platform-web/shell';

export interface PlatformWebAccessSnapshot {
  readonly modules: readonly { readonly id: string; readonly available: boolean }[];
  readonly permissions: readonly string[];
  readonly configurationValues: Readonly<Record<string, unknown>>;
}

export function validatePlatformWebModuleContributions(
  contributions: readonly PlatformWebModuleContribution[],
): void {
  const moduleIds = new Set<string>();
  const settingKeys = new Set<string>();
  const routeIds = new Set<string>();
  const navigationIds = new Set<string>();
  const slotIds = new Set<string>();
  const hostContracts = new Map<string, ReadonlySet<string>>();
  const launcherContracts = new Set<string>();
  const launcherOwners = new Set<string>();
  for (const contribution of contributions) {
    for (const contract of contribution.hostContracts) {
      if (!contribution.manifest.provides.includes(contract.capability)) {
        throw new Error(
          `Web module ${contribution.moduleId} cannot register unprovided host capability ${contract.capability}.`,
        );
      }
      if (hostContracts.has(contract.capability)) {
        throw new Error(`Duplicate web host capability: ${contract.capability}`);
      }
      const slots = new Set(contract.slots);
      if (
        slots.size === 0 ||
        slots.size !== contract.slots.length ||
        contract.slots.some((slot) => !/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+$/.test(slot))
      ) {
        throw new Error(`Web host capability ${contract.capability} has invalid slots.`);
      }
      hostContracts.set(contract.capability, slots);
    }
    for (const contract of contribution.launcherContracts ?? []) {
      if (!contribution.manifest.provides.includes(contract.capability)) {
        throw new Error(
          `Web module ${contribution.moduleId} cannot register unprovided launcher capability ${contract.capability}.`,
        );
      }
      if (launcherContracts.has(contract.capability)) {
        throw new Error(`Duplicate web launcher capability: ${contract.capability}`);
      }
      launcherContracts.add(contract.capability);
    }
  }
  for (const contribution of contributions) {
    if (moduleIds.has(contribution.moduleId)) {
      throw new Error(`Duplicate web module contribution: ${contribution.moduleId}`);
    }
    moduleIds.add(contribution.moduleId);
    if (contribution.manifest.id !== contribution.moduleId) {
      throw new Error(
        `Web module contribution ${contribution.moduleId} carries manifest ${contribution.manifest.id}.`,
      );
    }
    const declaredConfigurationKeys = new Set([
      ...contribution.manifest.settings,
      ...contribution.manifest.copyCatalogs,
    ]);
    const registeredConfigurationKeys = new Set<string>();
    for (const { definition } of contribution.settingRegistrations) {
      if (definition.moduleId !== contribution.moduleId) {
        throw new Error(
          `Web module ${contribution.moduleId} cannot register setting ${definition.key} for module ${definition.moduleId}.`,
        );
      }
      if (!declaredConfigurationKeys.has(definition.key)) {
        throw new Error(
          `Web module ${contribution.moduleId} registers undeclared setting ${definition.key}.`,
        );
      }
      if (settingKeys.has(definition.key)) {
        throw new Error(`Duplicate web setting registration: ${definition.key}`);
      }
      settingKeys.add(definition.key);
      registeredConfigurationKeys.add(definition.key);
    }
    for (const declaredKey of declaredConfigurationKeys) {
      if (!registeredConfigurationKeys.has(declaredKey)) {
        throw new Error(
          `Web module ${contribution.moduleId} has no registration for ${declaredKey}.`,
        );
      }
    }

    const declaredRoutes = new Set(contribution.manifest.web.routes.map(({ id }) => id));
    const implementedRoutes = new Set<string>();
    for (const route of contribution.routes) {
      if (route.moduleId !== contribution.moduleId) {
        throw new Error(`Web route ${route.id} belongs to ${route.moduleId}, not ${contribution.moduleId}.`);
      }
      if (!declaredRoutes.has(route.id)) {
        throw new Error(`Web route ${route.id} is not declared by ${contribution.moduleId}.`);
      }
      if (routeIds.has(route.id)) throw new Error(`Duplicate web route id: ${route.id}`);
      routeIds.add(route.id);
      implementedRoutes.add(route.id);
      if (!contribution.manifest.permissions.includes(route.requiredPermission)) {
        throw new Error(
          `Web route ${route.id} requires undeclared permission ${route.requiredPermission}.`,
        );
      }
      if (
        route.settingCondition !== undefined &&
        route.settingCondition !== null &&
        !declaredConfigurationKeys.has(route.settingCondition.key)
      ) {
        throw new Error(
          `Web route ${route.id} conditions on undeclared setting ${route.settingCondition.key}.`,
        );
      }
      if (route.navigation !== null) {
        const declaration = contribution.manifest.web.navigation.find(
          ({ id }) => id === route.navigation!.id,
        );
        if (declaration === undefined || declaration.priority !== route.navigation.priority) {
          throw new Error(`Web navigation ${route.navigation.id} does not match its manifest.`);
        }
        if (navigationIds.has(route.navigation.id)) {
          throw new Error(`Duplicate web navigation id: ${route.navigation.id}`);
        }
        navigationIds.add(route.navigation.id);
      }
    }
    for (const routeId of declaredRoutes) {
      if (!implementedRoutes.has(routeId)) {
        throw new Error(`Web route ${contribution.moduleId}:${routeId} has no implementation.`);
      }
    }
    for (const navigation of contribution.manifest.web.navigation) {
      if (!contribution.routes.some((route) => route.navigation?.id === navigation.id)) {
        throw new Error(
          `Web navigation ${contribution.moduleId}:${navigation.id} has no route implementation.`,
        );
      }
    }

    const declarations = new Map(
      contribution.manifest.web.slots.map((declaration) => [declaration.id, declaration]),
    );
    const implementedSlotIds = new Set<string>();
    for (const editor of contribution.settingEditors) {
      if (implementedSlotIds.has(editor.id)) {
        throw new Error(`Duplicate web slot implementation: ${editor.id}`);
      }
      const declaration = declarations.get(editor.id);
      if (editor.moduleId !== contribution.moduleId) {
        throw new Error(
          `Setting editor ${editor.id} belongs to ${editor.moduleId}, not ${contribution.moduleId}.`,
        );
      }
      if (!registeredConfigurationKeys.has(editor.settingKey)) {
        throw new Error(
          `Setting editor ${editor.id} targets unregistered setting ${editor.settingKey}.`,
        );
      }
      if (
        declaration === undefined ||
        declaration.priority !== editor.priority ||
        declaration.requiredCapability !== undefined ||
        declaration.requiredPermission !== undefined
      ) {
        throw new Error(`Setting editor ${editor.id} does not match its manifest slot.`);
      }
      implementedSlotIds.add(editor.id);
    }
    for (const host of contribution.hostContributions) {
      if (implementedSlotIds.has(host.id)) {
        throw new Error(`Duplicate web slot implementation: ${host.id}`);
      }
      const declaration = declarations.get(host.id);
      if (host.moduleId !== contribution.moduleId) {
        throw new Error(
          `Web host contribution ${host.id} belongs to ${host.moduleId}, not ${contribution.moduleId}.`,
        );
      }
      if (
        declaration === undefined ||
        declaration.priority !== host.priority ||
        declaration.requiredCapability !== host.contractVersion ||
        (declaration.requiredPermission ?? null) !== host.requiredPermission
      ) {
        throw new Error(`Web host contribution ${host.id} does not match its manifest slot.`);
      }
      if (
        host.requiredPermission !== null &&
        !contribution.manifest.permissions.includes(host.requiredPermission)
      ) {
        throw new Error(
          `Web host contribution ${host.id} requires undeclared permission ${host.requiredPermission}.`,
        );
      }
      if (
        host.settingCondition !== null &&
        !registeredConfigurationKeys.has(host.settingCondition.key)
      ) {
        throw new Error(
          `Web host contribution ${host.id} targets unregistered setting ${host.settingCondition.key}.`,
        );
      }
      const hostSlots = hostContracts.get(host.contractVersion);
      const requiredHost = contribution.manifest.requiredCapabilities.includes(host.contractVersion);
      const optionalHost = contribution.manifest.optionalCapabilities.includes(host.contractVersion);
      if (!requiredHost && !optionalHost) {
        throw new Error(
          `Web host contribution ${contribution.moduleId}:${host.id} has no declared host capability.`,
        );
      }
      if (hostSlots === undefined && requiredHost) {
        throw new Error(
          `Web host contribution ${contribution.moduleId}:${host.id} has no declared host capability.`,
        );
      }
      if (hostSlots !== undefined && !hostSlots.has(host.slot)) {
        throw new Error(
          `Web host contribution ${host.id} targets undeclared slot ${host.slot}.`,
        );
      }
      if (host.launcher !== undefined && host.launcher !== null) {
        const launcherCapability = host.launcher.contractVersion;
        const launcherOwner = `${launcherCapability}:${contribution.moduleId}`;
        if (launcherOwners.has(launcherOwner)) {
          throw new Error(
            `Web module ${contribution.moduleId} has multiple launchers for ${launcherCapability}.`,
          );
        }
        launcherOwners.add(launcherOwner);
        const requiredLauncher = contribution.manifest.requiredCapabilities.includes(
          launcherCapability,
        );
        const optionalLauncher = contribution.manifest.optionalCapabilities.includes(
          launcherCapability,
        );
        if (!requiredLauncher && !optionalLauncher) {
          throw new Error(
            `Web launcher contribution ${contribution.moduleId}:${host.id} has no declared launcher capability.`,
          );
        }
        if (!launcherContracts.has(launcherCapability) && requiredLauncher) {
          throw new Error(
            `Web launcher contribution ${contribution.moduleId}:${host.id} has no registered launcher capability.`,
          );
        }
        if (
          host.launcher.supportedSizes.length === 0 ||
          new Set(host.launcher.supportedSizes).size !== host.launcher.supportedSizes.length ||
          !host.launcher.supportedSizes.includes(host.launcher.defaultSize)
        ) {
          throw new Error(`Web launcher contribution ${host.id} has invalid size bounds.`);
        }
      }
      implementedSlotIds.add(host.id);
    }
    for (const declaration of contribution.manifest.web.slots) {
      if (!implementedSlotIds.has(declaration.id)) {
        throw new Error(
          `Web slot contribution ${contribution.moduleId}:${declaration.id} has no implementation.`,
        );
      }
      if (slotIds.has(declaration.id)) throw new Error(`Duplicate web slot id: ${declaration.id}`);
      slotIds.add(declaration.id);
    }
  }
}

function webEntrypoint(moduleId: string): string {
  if (moduleId === 'platform') {
    return '@crest/platform-web/web-catalog#platformWebModuleContribution';
  }
  const camel = moduleId.replace(/-([a-z0-9])/g, (_match, character: string) =>
    character.toUpperCase());
  return `@crest/module-${moduleId}/web-catalog#${camel}WebModuleContribution`;
}

export function buildPlatformWebContributionMetadata(
  contributions: readonly PlatformWebModuleContribution[],
): readonly PlatformModuleContributionMetadata[] {
  validatePlatformWebModuleContributions(contributions);
  return contributions.flatMap((contribution) => {
    const moduleId = contribution.moduleId;
    const entrypoint = webEntrypoint(moduleId);
    const routeDeclarations = new Map(
      contribution.manifest.web.routes.map((declaration) => [declaration.id, declaration]),
    );
    const navigationDeclarations = new Map(
      contribution.manifest.web.navigation.map((declaration) => [declaration.id, declaration]),
    );
    const slotDeclarations = new Map(
      contribution.manifest.web.slots.map((declaration) => [declaration.id, declaration]),
    );
    const metadata: PlatformModuleContributionMetadata[] = [];
    for (const route of contribution.routes) {
      const declaration = routeDeclarations.get(route.id)!;
      const condition = route.settingCondition ?? null;
      metadata.push(definePlatformModuleContributionMetadata({
        moduleId,
        id: route.id,
        surface: 'web',
        kind: 'route',
        slot: route.path,
        capability: declaration.requiredCapability ?? null,
        permission: route.requiredPermission,
        settingCondition: condition,
        priority: declaration.priority,
        entrypoint: `${entrypoint}.routes[${route.id}].load`,
        loading: 'lazy',
        contractVersion: contribution.manifest.version,
        emptyBehavior: 'not-found',
        errorBehavior: 'platform-error',
      }));
      if (route.navigation !== null) {
        const navigationDeclaration = navigationDeclarations.get(route.navigation.id)!;
        metadata.push(definePlatformModuleContributionMetadata({
          moduleId,
          id: route.navigation.id,
          surface: 'web',
          kind: 'navigation',
          slot: 'platform.web.navigation.primary',
          capability: navigationDeclaration.requiredCapability ?? null,
          permission: route.requiredPermission,
          settingCondition: condition,
          priority: route.navigation.priority,
          entrypoint: `${entrypoint}.routes[${route.id}].navigation`,
          loading: 'eager-composition',
          contractVersion: contribution.manifest.version,
          emptyBehavior: 'omit',
          errorBehavior: 'collapse',
        }));
      }
    }
    for (const editor of contribution.settingEditors) {
      const declaration = slotDeclarations.get(editor.id)!;
      metadata.push(definePlatformModuleContributionMetadata({
        moduleId,
        id: editor.id,
        surface: 'web',
        kind: 'setting-editor',
        slot: 'platform.web.settings.editors',
        capability: declaration.requiredCapability ?? null,
        permission: declaration.requiredPermission ?? null,
        settingCondition: null,
        priority: editor.priority,
        entrypoint: `${entrypoint}.settingEditors[${editor.id}].load`,
        loading: 'lazy',
        contractVersion: contribution.manifest.version,
        emptyBehavior: 'inline',
        errorBehavior: 'inline',
      }));
    }
    for (const host of contribution.hostContributions) {
      metadata.push(definePlatformModuleContributionMetadata({
        moduleId,
        id: host.id,
        surface: 'web',
        kind: 'host',
        slot: host.slot,
        capability: host.contractVersion,
        permission: host.requiredPermission,
        settingCondition: host.settingCondition,
        priority: host.priority,
        entrypoint: `${entrypoint}.hostContributions[${host.id}].load`,
        loading: 'lazy',
        contractVersion: contribution.manifest.version,
        emptyBehavior: host.emptyBehavior,
        errorBehavior: host.errorBehavior,
      }));
    }
    return metadata;
  });
}

export function buildPlatformWebSettingEditorContributions(
  contributions: readonly PlatformWebModuleContribution[],
): ReadonlyMap<string, PlatformWebSettingEditorContribution> {
  validatePlatformWebModuleContributions(contributions);
  const editorIds = new Set<string>();
  const editorBySettingKey = new Map<string, PlatformWebSettingEditorContribution>();
  for (const contribution of contributions) {
    for (const editor of contribution.settingEditors) {
      if (editorIds.has(editor.id)) {
        throw new Error(`Duplicate web setting editor id: ${editor.id}`);
      }
      if (editorBySettingKey.has(editor.settingKey)) {
        throw new Error(`Duplicate web setting editor for: ${editor.settingKey}`);
      }
      editorIds.add(editor.id);
      editorBySettingKey.set(editor.settingKey, editor);
    }
  }
  return editorBySettingKey;
}

export function getAvailablePlatformWebRoutes(
  routes: readonly PlatformWebRouteContribution[],
  access: Pick<PlatformWebAccessSnapshot, 'modules' | 'permissions'> &
    Partial<Pick<PlatformWebAccessSnapshot, 'configurationValues'>>,
): readonly PlatformWebRouteContribution[] {
  const availableModuleIds = new Set(
    access.modules.filter(({ available }) => available).map(({ id }) => id),
  );
  const configurationValues = access.configurationValues ?? {};
  return routes.filter((route) => {
    const condition = route.settingCondition;
    const configured = condition === undefined || condition === null
      ? undefined
      : configurationValues[condition.key];
    const actual = condition === undefined || condition === null
      ? undefined
      : condition.property === null
        ? configured
        : typeof configured === 'object' && configured !== null && !Array.isArray(configured)
          ? (configured as Readonly<Record<string, unknown>>)[condition.property]
          : undefined;
    return (
      availableModuleIds.has(route.moduleId) &&
      access.permissions.includes(route.requiredPermission) &&
      (condition === undefined || condition === null || Object.is(actual, condition.equals))
    );
  });
}

export function getPlatformWebConfigurationLabel(
  values: Readonly<Record<string, unknown>>,
  key: string,
  property: string,
  fallback: string,
): string {
  const configured = values[key];
  if (typeof configured !== 'object' || configured === null || Array.isArray(configured)) {
    return fallback;
  }
  const value = (configured as Readonly<Record<string, unknown>>)[property];
  return typeof value === 'string' && value.trim() !== '' ? value : fallback;
}

export function buildPlatformWebNavigation(
  routes: readonly PlatformWebRouteContribution[],
  configurationValues: Readonly<Record<string, unknown>>,
): PlatformNavigationItem[] {
  type NavigationGroup = Readonly<{
    id: string;
    copyProperty: string;
    label: string;
    priority: number;
  }>;

  // These IDs are presentation slots consumed by PlatformShell. Keep them
  // stable while their default labels and ordering mirror Water Pro's
  // operating vocabulary. A tenant-provided group label still wins below.
  const groups: Readonly<Record<string, NavigationGroup>> = {
    command: {
      id: 'command', copyProperty: 'navigationGroupCommand',
      label: 'Command', priority: 0,
    },
    projects: {
      id: 'projects', copyProperty: 'navigationGroupProjects',
      label: 'Projects', priority: 1,
    },
    field: {
      id: 'field', copyProperty: 'navigationGroupField',
      label: 'Field', priority: 2,
    },
    money: {
      id: 'money', copyProperty: 'navigationGroupMoney',
      label: 'Money', priority: 3,
    },
    relationships: {
      id: 'relationships', copyProperty: 'navigationGroupRelationships',
      label: 'Customers & Growth', priority: 4,
    },
    'external-estimating': {
      id: 'external-estimating', copyProperty: 'navigationGroupEstimating',
      label: 'External Estimating', priority: 5,
    },
    reports: {
      id: 'reports', copyProperty: 'navigationGroupReports',
      label: 'Reports', priority: 6,
    },
    admin: {
      id: 'admin', copyProperty: 'navigationGroupAdmin',
      label: 'Admin', priority: 7,
    },
  };

  const configuredCopyValue = (
    key: string,
    property: string,
  ): string | undefined => {
    const configured = configurationValues[key];
    if (typeof configured !== 'object' || configured === null || Array.isArray(configured)) {
      return undefined;
    }
    const value = (configured as Readonly<Record<string, unknown>>)[property];
    return typeof value === 'string' && value.trim() !== '' ? value : undefined;
  };

  const groupLabel = (group: NavigationGroup): string => {
    return configuredCopyValue('platform.copy.core', group.copyProperty) ?? group.label;
  };

  const group = (id: string): Readonly<{ id: string; label: string }> => {
    const definition = groups[id] ?? groups.admin!;
    return { id: definition.id, label: groupLabel(definition) };
  };

  const preferredLabels: Readonly<Record<string, string>> = {
    'projects.web.projects': 'Projects',
    'projects.web.board': 'Board',
    'assets.web.registry': 'Equipment',
    'mitigation.web.psychrometrics': 'Psychrometric',
    'estimating.web.estimates': 'Estimates',
    'estimating.web.pricebook': 'Price Book',
    'finance.web.reconciliation': 'Bank Audit',
    'procurement.web.orders': 'Purchase Orders',
    'workforce.web.directory': 'Staff',
    'knowledge.web.library': 'Company Library',
  };

  const presentationLabel = (route: PlatformWebRouteContribution): string => {
    const navigation = route.navigation!;
    if (route.id === 'projects.web.projects') {
      return configuredCopyValue('platform.copy.core', 'navigationProjects')
        ?? preferredLabels[route.id]
        ?? navigation.fallbackLabel;
    }
    const configured = configuredCopyValue(
      navigation.labelConfigurationKey,
      navigation.labelValueProperty,
    );
    const preferred = preferredLabels[route.id];
    return configured ?? preferred ?? navigation.fallbackLabel;
  };

  // Module priorities are useful as a fallback, but they are not a complete
  // cross-module IA. These positions reproduce the familiar legacy sequence
  // wherever the refactored catalog has an equivalent route.
  const preferredOrder: Readonly<Record<string, number>> = {
    'platform.web.my-day': 0,
    'tasks.web.focus': 10,
    'timesheets.web.clock': 20,
    'timesheets.web.activity-review': 30,
    'knowledge.web.library': 50,
    'training.web.center': 70,
    'assistant.web.workspace': 80,
    'tasks.web.notifications': 90,

    'relationships.web.leads': 0,
    'projects.web.projects': 10,
    'projects.web.board': 20,
    'scheduling.web.events': 30,
    'tasks.web.work-queue': 60,

    'assets.web.registry': 10,
    'assets.web.inventory': 20,
    'assets.web.placements': 30,
    'assets.web.location-review': 40,
    'mitigation.web.psychrometrics': 50,

    'estimating.web.estimates': 10,
    'estimating.web.proposals': 15,
    'finance.web.invoices': 20,
    'finance.web.payments': 30,
    'estimating.web.pricebook': 50,
    'estimating.web.estimate-templates': 60,
    'procurement.web.orders': 70,
    'procurement.web.supplier-operations': 80,
    'finance.web.expenses': 90,
    'finance.web.overhead': 100,
    'finance.web.reconciliation': 110,

    'relationships.web.contacts': 0,
    'relationships.web.organizations': 10,
    'finance.web.referral-commissions': 20,

    'reporting.web.definitions': 0,
    'finance.web.project-profitability': 10,

    'workforce.web.directory': 0,
    'workforce.web.protected-forms': 10,
    'workflows.web.rules': 20,
    'documents.web.instances': 30,
    'documents.web.templates': 40,
    'documents.web.training-records': 50,
    'documents.web.protected-forms': 60,
    'documents.web.signed-policies': 70,
    'training.web.management': 80,
    'training.web.grades': 90,
    'training.web.certifications': 100,
    'knowledge.web.management': 110,
    'knowledge.web.governance': 120,
    'timesheets.web.lunch-review': 130,
    'timesheets.web.location-review': 140,
    'timesheets.web.entry-split': 150,
  };

  const navigationGroup = (
    route: PlatformWebRouteContribution,
  ): Readonly<{ id: string; label: string }> => {
    if (route.id === 'knowledge.web.library') {
      return group('command');
    }
    if (route.id === 'timesheets.web.activity-review') {
      return group('command');
    }
    if ([
      'timesheets.web.lunch-review',
      'timesheets.web.location-review',
      'timesheets.web.entry-split',
    ].includes(route.id)) {
      return group('admin');
    }
    if (['tasks.web.work-queue', 'relationships.web.leads'].includes(route.id)) {
      return group('projects');
    }
    if (route.id === 'finance.web.project-profitability') {
      return group('reports');
    }
    if (route.id === 'finance.web.referral-commissions') {
      return group('relationships');
    }
    if (['estimating.web.estimates', 'estimating.web.proposals', 'estimating.web.pricebook',
      'estimating.web.estimate-templates'].includes(route.id)) {
      return group('money');
    }
    if (route.id === 'training.web.center') {
      return group('command');
    }
    const { moduleId } = route;
    if (['platform', 'tasks', 'timesheets', 'assistant'].includes(moduleId)) {
      return group('command');
    }
    if (['projects', 'scheduling'].includes(moduleId)) {
      return group('projects');
    }
    if (['scanning-media', 'mitigation', 'assets'].includes(moduleId)) {
      return group('field');
    }
    if (['finance', 'procurement'].includes(moduleId)) {
      return group('money');
    }
    if (['relationships', 'communications', 'customer-portal'].includes(moduleId)) {
      return group('relationships');
    }
    if (['estimating', 'insurance-claims'].includes(moduleId)) {
      return group('external-estimating');
    }
    if (moduleId === 'reporting') {
      return group('reports');
    }
    return group('admin');
  };
  return routes
    .filter((route) => route.navigation !== null)
    .map((route) => {
      const group = navigationGroup(route);
      return { route, group };
    })
    .sort((left, right) =>
      (groups[left.group.id]?.priority ?? 99) - (groups[right.group.id]?.priority ?? 99) ||
      (preferredOrder[left.route.id] ?? 1000) - (preferredOrder[right.route.id] ?? 1000) ||
      left.route.navigation!.priority - right.route.navigation!.priority ||
      left.route.id.localeCompare(right.route.id))
    .map(({ route, group }) => ({
      id: route.navigation!.id,
      label: presentationLabel(route),
      to: route.path,
      groupId: group.id,
      groupLabel: group.label,
    }));
}
